import React, { useEffect, useState } from 'react';
import * as S from './Table.styled';
import { Badge, Button, Popconfirm, Table as TableAnt, Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { TablePaginationConfig } from 'antd/lib/table/interface';
import {
	ICategory,
	ICompletedStatus,
	IRegion,
	IFurnishingStatus,
	ILot,
	ILotCover,
	IPurpose,
	IType,
} from 'interfaces/main';
import type { FilterValue, SortOrder, SorterResult } from 'antd/es/table/interface';
import { clearUnit, numberWithCommas } from 'utils/help';
import { useAppSelector } from 'hooks/useStore';
import { LibrariesSelector } from 'features/Libraries/LibrariesSlice';
import { uniq } from 'lodash';

const { Text } = Typography;

interface TableParams {
	pagination?: TablePaginationConfig;
	sortField?: string;
	sortOrder?: SortOrder;
	filters?: Record<string, FilterValue>;
}

export interface Props {
	items: ILot[];
	isDisabled: boolean;
	onEdit(lotId: number): void;
	onRemove(lotId: number): void;
}

const Table: React.FC<Props> = ({ items, isDisabled, onEdit, onRemove }) => {
	// Table params
	const [tableParams, setTableParams] = useState<TableParams>({
		pagination: {
			current: 1,
			pageSize: 10,
			total: 0,
		},
		// sortField: "createdAt",
		// sortOrder: "descend",
	});

	// Словари
	const {
		furnishingStatuses: allFurnishingStatuses,
		completedStatuses: allCompletedStatuses,
		types: allTypes,
		purposes: allPurposes,
		categories: allCategories,
		regions: allRegions,
	} = useAppSelector(LibrariesSelector);

	useEffect(
		() =>
			setTableParams({
				...tableParams,
				pagination: {
					...tableParams.pagination,
					total: items.length,
				},
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[items],
	);

	const rooms = uniq(items.map((item: ILot) => item.rooms)).map((rooms: number) => ({
		text: rooms === 0 ? 'Studio' : rooms,
		value: rooms,
	}));

	// const phones = uniq(items.map((item: ILot) => item.phone)).map(
	//   (phone: string) => ({ text: phone, value: phone })
	// );

	// const names = uniq(items.map((item: ILot) => item.name)).map(
	//   (name: string) => ({ text: name, value: name })
	// );

	// Конфиг таблички для Ant Table
	const columns: ColumnsType<ILot> = [
		// {
		//   title: 'Title',
		//   dataIndex: 'title',
		//   key: 'title',
		//   ellipsis: true,
		//   width: 150,
		//   render: (title: string) => title || '-',
		// },
		{
			title: '',
			dataIndex: 'id',
			key: 'id',
			width: 80,
			fixed: 'left',
			render: (id: number) => <Text type="secondary">{id}</Text>,
		},
		{
			title: 'Status',
			dataIndex: 'isArchived',
			key: 'isArchived',
			width: 80,
			fixed: 'left',
			render: (isArchived: boolean) => <Badge status={isArchived ? 'error' : 'success'} />,
		},
		{
			title: 'Unit',
			dataIndex: 'unit',
			key: 'unit',
			width: 100,
			fixed: 'left',
			sorter: (a: ILot, b: ILot) => Number(clearUnit(a.unit)) - Number(clearUnit(b.unit)),
			defaultSortOrder: 'ascend',
			render: (unit: string) => unit || '-',
		},
		{
			title: 'Floor',
			dataIndex: 'floor',
			key: 'floor',
			width: 100,
			sorter: (a: ILot, b: ILot) => {
				if (a.floor < b.floor) {
					return -1;
				}
				if (a.floor > b.floor) {
					return 1;
				}
				return 0;
			},
			render: (floor: number | null) => (floor === null ? '-' : floor),
		},
		{
			title: 'Plan',
			dataIndex: 'lotCover',
			key: 'lotCover',
			width: 80,
			render: (lotCover: ILotCover) => <Badge status={!lotCover ? 'error' : 'success'} />,
		},
		// {
		//   title: 'Description',
		//   dataIndex: 'description',
		//   key: 'description',
		//   ellipsis: true,
		//   onFilter: (value: string, lot: ILot) => lot.description === value,
		//   width: 150,
		//   render: (description: string) => description || '-',
		// },
		{
			title: 'Tot. area, sqft',
			dataIndex: 'totalArea',
			key: 'totalArea',
			width: 130,
			render: (totalArea: number, lot: ILot) => {
				const exArea = lot?.balconyArea || 0;
				const intArea = lot?.suiteArea || 0;
				const totArea = lot?.totalArea || 0;
				const dif = Math.abs(totArea - (intArea + exArea));
				const isTrue = dif <= 1.02;

				return (
					<Text type={isTrue ? 'success' : 'danger'}>
						{numberWithCommas(totalArea)}
						{isTrue ? '' : ` (${dif})`}
					</Text>
				);
			},
		},
		{
			title: 'Int. area, sqft',
			dataIndex: 'suiteArea',
			key: 'suiteArea',
			width: 130,
			render: (suiteArea: number | null) =>
				suiteArea !== null ? `${numberWithCommas(suiteArea)}` : '-',
		},
		{
			title: 'Ext. area, sqft',
			dataIndex: 'balconyArea',
			key: 'balconyArea',
			width: 130,
			render: (balconyArea: number | null) =>
				balconyArea !== null ? `${numberWithCommas(balconyArea)}` : '-',
		},
		{
			title: 'Pl. area, sqft',
			dataIndex: 'plotArea',
			key: 'plotArea',
			width: 130,
			render: (plotArea: number | null) =>
				plotArea !== null ? `${numberWithCommas(plotArea)}` : '-',
		},
		{
			title: 'Bedrooms',
			dataIndex: 'rooms',
			key: 'rooms',
			filters: rooms,
			// sorter: (a: ILot, b: ILot) => a.rooms - b.rooms,
			// defaultSortOrder: 'ascend',
			onFilter: (value: number, lot: ILot) => lot.rooms === value,
			width: 130,
			render: (rooms: number | null) => (rooms !== null ? (rooms === 0 ? 'Studio' : rooms) : '-'),
		},
		{
			title: 'Plan type',
			dataIndex: 'planType',
			key: 'planType',
			// filters: rooms,
			// sorter: (a: ILot, b: ILot) => a.rooms - b.rooms,
			// defaultSortOrder: 'ascend',
			onFilter: (value: string, lot: ILot) => lot.planType === value,
			width: 130,
			render: (planType: string | null) => (planType !== null ? planType : '-'),
		},
		// {
		//   title: 'Baths',
		//   dataIndex: 'baths',
		//   key: 'baths',
		//   width: 80,
		//   render: (baths: number) => baths || '-',
		// },
		{
			title: 'Price, USD',
			dataIndex: 'price',
			key: 'price',
			width: 150,
			render: (price: number) => numberWithCommas(price),
		},
		{
			title: 'Purpose',
			dataIndex: 'purposeId',
			key: 'purposeId',
			filters: allPurposes.map((item: IPurpose) => ({
				text: item.name_en,
				value: item.id,
			})),
			onFilter: (value: number, lot: ILot) => lot.purposeId === value,
			width: 130,
			render: (purposeId: number) =>
				!!purposeId ? (
					<Tag
						bordered={false}
						color={allPurposes.find((p: IPurpose) => p.id === purposeId).antColor}>
						{allPurposes.find((p: IPurpose) => p.id === purposeId).name_en}
					</Tag>
				) : (
					'-'
				),
		},
		{
			title: 'Furnishing',
			dataIndex: 'furnishingStatusId',
			key: 'furnishingStatusId',
			filters: allFurnishingStatuses.map((item: IFurnishingStatus) => ({
				text: item.name_en,
				value: item.id,
			})),
			width: 140,
			onFilter: (value: number, lot: ILot) => lot.furnishingStatusId === value,
			render: (furnishingStatusId: number) =>
				!!furnishingStatusId ? (
					<Tag
						bordered={false}
						color={
							allFurnishingStatuses.find((p: IFurnishingStatus) => p.id === furnishingStatusId)
								.antColor
						}>
						{
							allFurnishingStatuses.find((p: IFurnishingStatus) => p.id === furnishingStatusId)
								.name_en
						}
					</Tag>
				) : (
					'-'
				),
		},
		{
			title: 'Completed',
			dataIndex: 'completedStatusId',
			key: 'completedStatusId',
			filters: allCompletedStatuses.map((item: ICompletedStatus) => ({
				text: item.name_en,
				value: item.id,
			})),
			width: 180,
			onFilter: (value: number, lot: ILot) => lot.completedStatusId === value,
			render: (completedStatusId: number) =>
				!!completedStatusId ? (
					<Tag
						bordered={false}
						color={
							allCompletedStatuses.find((p: ICompletedStatus) => p.id === completedStatusId)
								.antColor
						}>
						{allCompletedStatuses.find((p: ICompletedStatus) => p.id === completedStatusId).name_en}
					</Tag>
				) : (
					'-'
				),
		},
		{
			title: 'Type',
			dataIndex: 'typeId',
			key: 'typeId',
			filters: allTypes.map((item: IType) => ({
				text: item.name_en,
				value: item.id,
			})),
			onFilter: (value: number, lot: ILot) => lot.typeId === value,
			width: 150,
			render: (typeId: number) =>
				!!typeId ? (
					<Tag bordered={false} color={allTypes.find((p: IType) => p.id === typeId).antColor}>
						{allTypes.find((p: IType) => p.id === typeId).name_en}
					</Tag>
				) : (
					'-'
				),
		},
		{
			title: 'Category',
			dataIndex: 'categoryId',
			key: 'categoryId',
			filters: allCategories.map((item: ICategory) => ({
				text: item.name,
				value: item.id,
			})),
			onFilter: (value: number, lot: ILot) => lot.categoryId === value,
			width: 150,
			render: (categoryId: number) =>
				!!categoryId ? (
					<Tag
						bordered={false}
						color={allCategories.find((c: ICategory) => c.id === categoryId).antColor}>
						{allCategories.find((c: ICategory) => c.id === categoryId).title_en}
					</Tag>
				) : (
					'-'
				),
		},
		{
			title: 'Region',
			dataIndex: 'regionId',
			key: 'regionId',
			filters: allRegions.map((item: IRegion) => ({
				text: item.name_en,
				value: item.id,
			})),
			width: 150,
			onFilter: (value: number, lot: ILot) => lot.regionId === value,
			render: (regionId: number) =>
				regionId !== null ? (
					<Tag
						bordered={false}
						color={allRegions.find((p: IRegion) => p.id === regionId).antColor}>
						{allRegions.find((p: IRegion) => p.id === regionId).name_en}
					</Tag>
				) : (
					'-'
				),
		},
		{
			title: '',
			key: 'action',
			fixed: 'right',
			width: 150,
			render: (_: string, lot: ILot) => {
				return (
					<S.Actions>
						<Button onClick={() => onEdit(lot.id)} size="small" disabled={isDisabled}>
							edit
						</Button>
						<Popconfirm
							placement="rightBottom"
							title="Are you sure to delete this lot?"
							onConfirm={() => onRemove(lot.id)}
							okText="delete"
							cancelText="cancel"
							disabled={isDisabled}>
							<Button size="small" disabled={isDisabled}>
								delete
							</Button>
						</Popconfirm>
					</S.Actions>
				);
			},
		},
	];

	const onHandleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue>,
		sorter: SorterResult<ILot>,
	) => {
		setTableParams({
			pagination,
			filters,
			...sorter,
		});
	};

	return (
		<S.Wrap>
			<TableAnt
				columns={columns}
				rowKey={(lot: ILot) => lot.id}
				dataSource={items}
				scroll={{ x: '1300' }}
				pagination={false}
				onChange={onHandleTableChange}
			/>
		</S.Wrap>
	);
};

export default Table;
